import { repliedLeadIds } from './activityStats';
import type { ActivitySlim } from './activityStats';
import { POSITION_ORDER, positionOf } from './leadPosition';
import type { LeadPosition } from './leadPosition';
import { leadStageKey } from './stageKeys';
import type { LeadDealOutcome } from './dealOutcomes';
import type { LeadFilter } from './leadFilters';
import type { Lead, LeadStage, Membership } from './types';

/**
 * One slice of leads, and what has actually happened to them.
 *
 * ── Why this is not the Dashboard ──────────────────────────────────────────
 *
 * Kyu, 31 Aug, after a huddle where Alex asked how the LinkedIn ad leads were
 * doing and the Hub could not answer:
 *
 *   "Dashboard is just a dashboard. A pipeline is more of, like, an
 *    in-the-weeds view."
 *   "there's many kinds of inbound"
 *
 * The Dashboard answers HOW ARE WE DOING across everything — every lead in the
 * database, inbound and outbound in one lump. This answers WHAT IS HAPPENING
 * TO THESE EIGHT PEOPLE. Same data, different question, and the second one
 * cannot be got at by squinting at the first.
 *
 * ── It adds nothing and records nothing ────────────────────────────────────
 *
 * Kyu again: "The pipeline doesn't change anything. It actually reflects
 * what's already in there." Every number below is derived from something the
 * team already logs — the stage on the lead, the touches in the timeline, the
 * reply. No new field, no new tagging burden, nothing for anybody to remember.
 *
 * Which also means it is a mirror: point it at a source with bad origin tags
 * and it will show bad origin tags. That is the intended behaviour.
 */

/** The five bands, in the order work moves through them. */
/**
 * The bands, and why there are seven rather than five.
 *
 * Until 1 Sep this was STAGE_KEYS — the five statuses a human sets — so a lead
 * whose deal had been won still sat under "Converted to Deal". That was
 * survivable while the Leads tab did the same thing. It stopped being
 * survivable on 31 Aug, when the Won/Lost mirror widened: from that moment the
 * Leads tab said Won and the Pipeline said Converted about the SAME lead, and
 * two screens disagreeing about one lead is the exact failure this screen was
 * built to end.
 *
 * POSITION_ORDER is the shared vocabulary — the five a human sets plus the two
 * the deals decide — so both screens now read a lead the same way by
 * construction rather than by anybody remembering to keep them in step.
 */
export const PIPELINE_BANDS = POSITION_ORDER;

export interface PipelineRow {
  leadId: string;
  name: string;
  company: string;
  /** Day the lead arrived, as a plain YYYY-MM-DD. */
  arrivedOn: string;
  /** Day of the most recent touch, or null when nobody has contacted them. */
  lastTouchOn: string | null;
  /** Whole days between arriving and today. Only meaningful before first touch. */
  daysWaiting: number;
  /** True when they have answered — the step Alex asks about and no stage records. */
  replied: boolean;
  /**
   * True when at least one touch has been logged, from `touch_count`.
   *
   * NOT `lastTouchOn !== null`. Those are different questions and they gave
   * different answers on the live list: the sentence said 45 never contacted
   * while the band headings added up to 98, because `touch_count` is the
   * trigger-maintained counter and the timeline is the activity rows, and a
   * lead can carry a count with no activity row behind it — a touch ticked in
   * the Outreach grid, or an import that set the counter.
   *
   * The counter wins, because it is what the Leads list prints. One definition
   * of contacted, or the two screens describe different people.
   *
   * A REPLY also counts as contacted, whatever the counter says. Somebody who
   * answered was plainly reached — the outreach happened outside the Hub and
   * only the answer was recorded — and flagging them as untouched would send
   * a person to chase a live conversation.
   */
  contacted: boolean;
  /**
   * Why this lead was closed, or '' when it is not closed.
   *
   * Kyu's third question — "did anyone say not interested or fell off?" — and
   * the data was already guaranteed: `leads_closure_reason_guard` refuses to
   * close a lead without one. The band showed WHO was closed and hid the part
   * that answers the question.
   */
  closedBecause: string;
}

export interface PipelineBand {
  key: LeadPosition;
  rows: PipelineRow[];
  /**
   * How many rows in THIS band nobody has contacted.
   *
   * Not derivable from the band's name. A lead can sit on Contacted with no
   * touch ever logged — somebody moved the status by hand, or the import set
   * it — and that is exactly the case worth surfacing. The first version tied
   * the warning to the New band, which showed an amber New reading 0 beside a
   * sentence saying 2 never contacted. The mark has to follow the leads.
   */
  neverContacted: number;
}

export interface PipelineSummary {
  /** Everyone in the slice. The base for every percentage. */
  arrived: number;
  /** How many have at least one logged touch. */
  contacted: number;
  /** How many have answered. */
  replied: number;
  /**
   * How many reached a deal — converted, won OR lost.
   *
   * Counted from the position rather than from the Converted status alone.
   * A lead whose deal was won reached a deal; so did one whose deal was lost.
   * Counting only the still-open ones would have made this number FALL every
   * time a deal closed, which is the opposite of what anybody reading it
   * expects.
   */
  becameDeal: number;
  /*
   * `conversionRate` lived here and was deals / arrived. Two things killed it
   * on 1 Sep.
   *
   * It had been DEAD since the metric tiles came off in Drop 57 — computed on
   * every render, displayed nowhere, kept alive only by its own test.
   *
   * And it was the wrong definition. Kyu: "an inbound lead that becomes a deal
   * is very common. We should just use 1 conversion - if it gets WON." A
   * second, staler definition of the word sitting in the codebase is exactly
   * how the by-origin table came to print 42.9% next to a channel that has
   * closed nothing. One meaning, in one place — originBreakdown.ts.
   */
  /**
   * Arrived and never contacted.
   *
   * The one number that gets the accent. It is the failure the pipeline exists
   * to surface: a lead that came in through a paid ad and that nobody has
   * emailed is money spent and then dropped, and it is invisible on a funnel
   * whose base is "touched".
   */
  neverContacted: number;
  /** Per band, in STAGE_KEYS order. Empty bands are present, not omitted. */
  bands: PipelineBand[];
}

function dayOf(timestamp: string): string {
  return new Date(timestamp).toISOString().slice(0, 10);
}

function daysBetween(from: string, to: string): number {
  const ms = Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`);
  return Math.max(0, Math.round(ms / 86400000));
}

/**
 * Build the pipeline for a slice of leads.
 *
 * `leads` is expected to be ALREADY filtered — origin, direction, capture
 * method, date — by the same filter module the Leads table uses. That is
 * deliberate: two filter implementations would be two chances to disagree, and
 * a pipeline that disagrees with the list it links to is worse than no
 * pipeline. This function's only job is what happened to the people it is
 * handed.
 */
export function buildPipeline(
  leads: readonly Lead[],
  stages: readonly LeadStage[],
  activities: readonly ActivitySlim[],
  memberships: readonly Membership[],
  today: string,
  /**
   * The deal rollup, so Won and Lost can be read off the deals exactly as the
   * Leads tab reads them. An empty map is honest and safe: every lead then
   * reads at the status somebody set, which is what the screen showed before
   * the rollup existed.
   */
  outcomes: ReadonlyMap<string, LeadDealOutcome> = new Map(),
  /** The closing-reason vocabulary, so a closed row can say why. */
  reasons: readonly { id: string; name: string }[] = [],
): PipelineSummary {
  // Both routes to a reply: a logged activity, and the Outreach grid's reply
  // checkbox. Counting only one of them under-reports, and which one is
  // under-reported depends on which screen the person happened to use.
  const replied = repliedLeadIds([...activities], [...memberships]);
  // The WHOLE vocabulary, including anything deactivated: a reason somebody
  // retired still sits on every lead closed for it, and a blank there would
  // read as "no reason given" for a lead that has one.
  const reasonName = new Map(reasons.map((entry) => [String(entry.id), entry.name]));
  const reasonOf = (id: string | null) => (id === null ? '' : (reasonName.get(String(id)) ?? ''));

  // Latest touch per lead, from the timeline rather than the lead's own
  // last_touch_at, so a slice and its drawer cannot disagree.
  const lastTouch = new Map<string, string>();
  for (const activity of activities) {
    if (activity.kind === 'reply_received' || activity.kind === 'note') {
      continue;
    }
    const day = dayOf(activity.occurred_at);
    const held = lastTouch.get(activity.lead_id);
    if (held === undefined || day > held) {
      lastTouch.set(activity.lead_id, day);
    }
  }

  const bands = new Map<LeadPosition, PipelineRow[]>(PIPELINE_BANDS.map((key) => [key, []]));
  let contacted = 0;
  let neverContacted = 0;
  let becameDeal = 0;
  let repliedCount = 0;

  for (const lead of leads) {
    const arrivedOn = dayOf(lead.created_at);
    const touchDay = lastTouch.get(lead.id) ?? null;
    // touch_count is the trigger-maintained counter the Leads list prints, so
    // "contacted" means the same thing on both screens. The timeline supplies
    // the DATE; the counter decides the fact.
    const hasReplied = replied.has(lead.id);
    // One definition, used for the summary, the band headings and the row
    // mark. Three places counting slightly different things is how the
    // sentence came to say 45 while the bands added up to 98.
    const hasTouch = lead.touch_count > 0 || hasReplied;
    if (hasTouch) {
      contacted += 1;
    } else {
      neverContacted += 1;
    }
    if (hasReplied) {
      repliedCount += 1;
    }

    // The POSITION, not the raw status: the same function the Leads tab uses,
    // so the two screens cannot describe one lead differently.
    const key = positionOf(leadStageKey(lead.stage_id, [...stages]), outcomes.get(lead.id));
    if (key === 'converted' || key === 'won' || key === 'lost') {
      becameDeal += 1;
    }

    const name = `${lead.first_name} ${lead.last_name}`.trim();
    const row: PipelineRow = {
      leadId: lead.id,
      name: name === '' ? lead.email : name,
      company: lead.company_name,
      arrivedOn,
      lastTouchOn: touchDay,
      daysWaiting: daysBetween(arrivedOn, today),
      replied: hasReplied,
      contacted: hasTouch,
      closedBecause: reasonOf(lead.closure_reason_id),
    };

    // A lead whose status is not one of the five is NOT silently dropped into
    // the first band. stageKeys.ts is explicit about this: the old code's
    // `?? stages[0]` fallback is what let a broken vocabulary look like an
    // empty pipeline. An unrecognised status is left out of the bands and the
    // counts below say so.
    if (key !== null) {
      bands.get(key)?.push(row);
    }
  }

  for (const rows of bands.values()) {
    // Oldest first inside a band: the lead who has been waiting longest is the
    // one somebody should look at first, and it puts the worst case at the top
    // rather than buried at the bottom of a scroll.
    rows.sort((a, b) => a.arrivedOn.localeCompare(b.arrivedOn) || a.name.localeCompare(b.name));
  }

  return {
    arrived: leads.length,
    contacted,
    replied: repliedCount,
    becameDeal,
    neverContacted,
    bands: PIPELINE_BANDS.map((key) => {
      const rows = bands.get(key) ?? [];
      return {
        key,
        rows,
        // The same test the summary uses, so the band headings and the
        // sentence can never disagree again.
        neverContacted: rows.filter((row) => !row.contacted).length,
      };
    }),
  };
}

/**
 * The sentence somebody reads out in a huddle.
 *
 * This exists because the whole screen failed at one specific moment: Alex
 * asked, and Kyu had the data and no way to say it. So the answer is a string,
 * copyable, rather than five numbers somebody has to assemble under pressure.
 *
 * Never-emailed is only mentioned when there ARE any. A trailing "0 never
 * emailed" on a clean cohort is noise, and worse, it teaches the reader to
 * skip the end of the sentence — which is where the bad news lives.
 *
 * ── Why it says EMAILED and the band says CONTACTED ────────────────────────
 *
 * Because they are different facts and the screen said both with one word.
 * Kyu, 31 Aug, looking at the live page: "what the hell am I looking at?"
 *
 * Twenty pixels apart it read:
 *
 *   CONTACTED  122     <- the STATUS 122 leads are sitting on
 *   143 contacted      <- leads somebody has emailed at least once
 *
 * Both true, both useful, neither one wrong — and using the same word for
 * them made the screen look broken. A lead can be emailed and still sit on
 * New; a lead can sit on Contacted with nothing logged against it. So the
 * activity words are now EMAILED and NEVER EMAILED, and CONTACTED is left to
 * mean the status and nothing else.
 *
 * This is the same failure as the dashboard's "In range" versus "In pipeline
 * now" in August: two real things, one vocabulary, and a reader who cannot
 * tell which is which decides the whole screen is untrustworthy.
 */
export function pipelineSentence(summary: PipelineSummary, sliceLabel: string): string {
  const parts = [
    `${String(summary.arrived)} ${sliceLabel}`,
    `${String(summary.contacted)} reached out to`,
    `${String(summary.replied)} replied`,
    `${String(summary.becameDeal)} became ${summary.becameDeal === 1 ? 'a deal' : 'deals'}`,
  ];
  if (summary.neverContacted > 0) {
    parts.push(`${String(summary.neverContacted)} not reached out to`);
  }
  return parts.join(' · ');
}

/** What each band is called, and the one line under it saying what it means. */
export const BAND_LABELS: Record<LeadPosition, { title: string; meaning: string }> = {
  // Every meaning says STATUS, out loud. The bands are the five statuses a
  // lead can sit on; they are not activity counts, and the screen previously
  // let a reader believe they were.
  new: { title: 'New', meaning: 'status: nobody has worked them yet' },
  contacted: { title: 'Contacted', meaning: 'status: being worked, no answer yet' },
  followup: { title: 'Follow Up', meaning: 'status: still being worked, a next step is set' },
  engaged: { title: 'Engaged', meaning: 'status: they answered' },
  // The one this drop was built for. Kyu: hundreds of leads that are not
  // responding but we still cannot say hopeless. They used to read as
  // Contacted, which claimed work nobody was doing.
  nurture: { title: 'Nurture', meaning: 'status: alive, parked on purpose' },
  converted: { title: 'Converted to Deal', meaning: 'status: a live deal' },
  // Won and Lost are the two the DEALS decide, and the meaning says so rather
  // than saying "status" like the other five — because nobody sets these from
  // the dropdown and a reader who goes looking for them there will not find
  // them.
  won: { title: 'Won', meaning: 'their deal was won' },
  lost: { title: 'Lost', meaning: 'their deal was lost' },
  closed: { title: 'Closed', meaning: 'status: closed, with a reason' },
};

/**
 * What the current slice is called, for the sentence.
 *
 * Named from the filter that produced it, so the sentence says "8 from
 * Advertising > LinkedIn Ads" rather than "8 leads" — which is the difference
 * between an answer and a number.
 */
export function pipelineSliceLabel(
  // `Pick` off the real type, deliberately. The old signature was a loose
  // `{ originId?: string | null; source?: string | null }`, and LeadFilter has
  // no `source` — it has `direction`. A structural type with optional fields
  // accepts an object that has NEITHER, so the mismatch compiled, ran, and
  // silently returned "leads, all sources" on every tab since the day it
  // shipped. Kyu caught it on screen: the Inbound tab filtered to Advertising,
  // and the sentence underneath still read "7 leads, all sources".
  //
  // Picking the fields off LeadFilter makes a renamed or misspelled field a
  // compile error instead of a wrong sentence.
  filter: Pick<LeadFilter, 'direction' | 'originGroup' | 'originId'>,
  origins: readonly { id: string; name: string; group_name?: string | null }[],
): string {
  // The tab IS a filter. Describing a slice as everything when it plainly is
  // not is its own small way of making a screen untrustworthy.
  const direction = filter.direction === null ? null : filter.direction;

  // The platform if one is chosen, otherwise the group. Both are real answers:
  // "how is advertising doing" and "is LinkedIn beating Meta" are different
  // questions, and the sentence has to name whichever was asked.
  const platform =
    filter.originId == null ? undefined : origins.find((entry) => entry.id === filter.originId);
  const where =
    platform === undefined
      ? filter.originGroup
      : (platform.group_name ?? '').trim() === '' ||
          (platform.group_name ?? '').toLowerCase() === platform.name.toLowerCase()
        ? platform.name
        : `${(platform.group_name ?? '').trim()} › ${platform.name}`;

  if (where !== null && where !== undefined && where !== '') {
    return direction === null ? `from ${where}` : `${direction} from ${where}`;
  }
  if (direction !== null) {
    return `${direction} leads`;
  }
  return 'leads, all sources';
}
